import { type ReactNode, useId } from 'react';

type Tone = 'pass' | 'fail' | 'warn' | 'open' | 'default';
const valueTone: Record<Tone, string> = {
  pass: 'text-status-pass',
  fail: 'text-status-fail',
  warn: 'text-status-warn',
  open: 'text-text-muted',
  default: 'text-text-primary',
};

export function KpiCard(props: {
  repLabel: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: Tone;
  critical?: boolean;
  /** Erklaert, wie die Zahl zustande kommt. Kennzahlen, deren Nenner von einer pflegbaren
   *  Einstellung abhaengt (die Coverage haengt am Conversion-Scope der Prozesse), brauchen das:
   *  sonst nimmt jemand im Katalog ein Haekchen heraus und versteht die verschobene Quote nicht.
   *  Darstellung wie bei den Plan-Hinweisen (plan-detail.tsx): sichtbares ⓘ traegt den
   *  Maus-Tooltip und ist fuer Screenreader dekorativ, der Text haengt per aria-describedby an
   *  der Karte — so ist er auch ohne Maus erreichbar. */
  hint?: string;
}) {
  const tone = props.tone ?? 'default';
  const hintId = useId();
  return (
    <div
      aria-describedby={props.hint ? hintId : undefined}
      className={`rounded-card border bg-bg-surface p-4 ${
        props.critical
          ? 'border-[rgba(234,91,11,.3)] shadow-[0_0_20px_rgba(234,91,11,.12)]'
          : 'border-[var(--border)]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-text-muted">
          {props.label}
          {props.hint ? (
            <>
              <span
                aria-hidden="true"
                title={props.hint}
                className="cursor-help rounded-full border border-[var(--border)] px-1 text-[9px] leading-[1.4] text-text-muted"
              >
                i
              </span>
              <span id={hintId} className="sr-only">
                {props.hint}
              </span>
            </>
          ) : null}
        </span>
        <span className="font-mono text-[10.5px] text-text-muted">{props.repLabel}</span>
      </div>
      <div className={`mt-1 text-[28px] font-bold leading-tight ${valueTone[tone]}`}>
        {props.value}
      </div>
      {props.sub ? <div className="mt-1 text-xs text-text-muted">{props.sub}</div> : null}
    </div>
  );
}
